import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { ArrowRight, ScrollText, TrendingUp, ShieldCheck, Home as HomeIcon } from "lucide-react";

export default function VendreEnVenteATerme() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Vendre en vente à terme : guide complet 2026",
    "author": { "@type": "Organization", "name": "venteaterme.fr" },
    "publisher": { "@type": "Organization", "name": "venteaterme.fr" },
    "datePublished": "2026-01-15",
    "mainEntityOfPage": "https://venteaterme.fr/vendre-en-vente-a-terme",
  };
  return (
    <>
      <SEO
        title="Vendre en vente à terme : guide complet 2026 — venteaterme.fr"
        description="Tout comprendre pour vendre son bien immobilier en vente à terme : bouquet, mensualités, fiscalité, protections juridiques. Guide 2026 par venteaterme.fr."
        schema={schema}
      />
      <section className="pt-24 pb-14 bg-navy text-cream">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.3em] text-accent-brand font-bold mb-4">Guide pilier</div>
          <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight">Vendre en vente à terme&nbsp;: le guide complet 2026.</h1>
          <p className="text-cream/85 mt-8 max-w-3xl text-lg leading-relaxed">
            Pourquoi vendre en vente à terme, comment ça marche, quelle fiscalité, quelles protections&nbsp;? Tout ce qu'un
            vendeur doit savoir avant de se lancer — avec un standard éditorial clair&nbsp;:
            <strong className="text-accent-brand"> prix ferme, mensualités fixes, sans intérêts, sans indexation.</strong>
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-10 prose-vt text-navy/80">
          <h2>Le principe en une minute</h2>
          <p>
            La <strong>vente à terme</strong> est un mode de vente immobilière encadré par le Code civil. Le prix,
            ferme et définitif, est payé en deux temps&nbsp;: un <strong>bouquet</strong> à la signature de l'acte
            notarié, puis des <strong>mensualités fixes</strong> pendant une durée déterminée (5 à 30 ans). Contrairement
            au viager, <strong>il n'y a pas d'aléa de durée de vie</strong>. Contrairement à un prêt, <strong>il n'y a
            pas d'intérêts ni d'indexation</strong>.
          </p>

          <h2>Pourquoi vendre en vente à terme&nbsp;?</h2>
          <ul>
            <li><strong>Bouquet immédiat</strong> pour un projet, une donation ou une épargne.</li>
            <li><strong>Revenus fixes</strong> pendant la durée du contrat, versés mensuellement.</li>
            <li><strong>Fiscalité favorable</strong>&nbsp;: les mensualités correspondent au paiement échelonné d'un prix — elles ne sont pas imposables à l'IR ni soumises aux prélèvements sociaux.</li>
            <li><strong>Transmission protégée</strong>&nbsp;: en cas de décès, les mensualités continuent au profit des héritiers.</li>
          </ul>

          <h2>Fonctionnement en 5 étapes</h2>
          <ol>
            <li><strong>Estimation</strong> à la valeur vénale. Si vente occupée, décote d'occupation appliquée.</li>
            <li><strong>Négociation</strong> du triptyque bouquet / durée / mensualité. Prix ferme.</li>
            <li><strong>Compromis</strong> chez le notaire avec conditions suspensives usuelles.</li>
            <li><strong>Acte authentique</strong>&nbsp;: inscription de l'hypothèque légale spéciale (art. 2402 du Code civil) et de la clause résolutoire.</li>
            <li><strong>Paiement des mensualités</strong> pendant la durée fixée. Prix ferme, aucune indexation.</li>
          </ol>

          <h2>Vente libre ou vente occupée&nbsp;?</h2>
          <p>
            En <strong>vente libre</strong>, l'acheteur prend possession du bien dès la signature. En <strong>vente occupée</strong>,
            le vendeur conserve un Droit d'Usage et d'Habitation (DUH) ; une décote d'occupation vient réduire la valeur retenue
            (environ 5&nbsp;% + 1,5&nbsp;% par année d'occupation prévue, plafonnée à 40&nbsp;%).
          </p>

          <h2>Fiscalité côté vendeur</h2>
          <p>
            Le prix étant un capital échelonné, les mensualités ne sont <strong>pas imposables</strong>. La plus-value
            éventuelle suit le régime de droit commun (exonération pour résidence principale, abattement pour durée de
            détention). Le régime fiscal dépend toutefois de votre situation personnelle — validez-le avec votre notaire.
          </p>

          <h2>Protections juridiques</h2>
          <p>
            L'acte est signé chez notaire. Deux garanties majeures protègent le vendeur&nbsp;:
          </p>
          <ul>
            <li><strong>L'hypothèque légale spéciale (art. 2402 du Code civil)</strong> — inscription de 1er rang au fichier immobilier.</li>
            <li><strong>La clause résolutoire</strong> — permet de récupérer le bien en cas d'impayé, tout en conservant les mensualités déjà versées à titre de dommages-intérêts.</li>
          </ul>

          <h2>Vente à terme vs viager</h2>
          <p>
            La vente à terme diffère du viager sur un point essentiel&nbsp;: <strong>la durée est fixée d'avance</strong>.
            En viager, la rente s'éteint au décès du vendeur — un aléa constitutif du contrat. En vente à terme, les
            mensualités continuent au profit des héritiers jusqu'au terme convenu.
          </p>

          <h2>Prêt à vendre en vente à terme&nbsp;?</h2>
          <p>Commencez par une estimation gratuite en ligne, puis simulez votre opération avant de déposer votre annonce.</p>
        </div>
      </section>

      <section className="py-16 bg-navy text-cream">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <h2 className="font-serif text-3xl md:text-4xl mb-8">Passez à l'action</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { to: "/estimer", icon: TrendingUp, title: "Estimer mon bien", text: "Résultat immédiat + rapport détaillé" },
              { to: "/simulateur", icon: HomeIcon, title: "Simuler mon opération", text: "Bouquet, durée, mensualité en direct" },
              { to: "/deposer-annonce", icon: ScrollText, title: "Déposer mon annonce", text: "Publication après modération, 100% gratuit" },
            ].map((c, i) => (
              <Link key={i} to={c.to} className="p-6 bg-cream/5 border border-cream/15 hover:border-accent-brand transition-colors group">
                <c.icon className="text-accent-brand mb-4" size={30} strokeWidth={1.4} />
                <h3 className="font-serif text-xl text-cream mb-2 group-hover:text-accent-brand transition-colors">{c.title}</h3>
                <p className="text-sm text-cream/70">{c.text}</p>
                <div className="flex items-center gap-1 text-sm text-accent-brand mt-4 font-bold">Y aller <ArrowRight size={14} /></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <h2 className="font-serif text-2xl md:text-3xl text-navy mb-6">Vendre en vente à terme dans votre ville</h2>
          <div className="flex flex-wrap gap-2">
            {["paris","lyon","marseille","bordeaux","toulouse","nantes","nice","montpellier"].map((v) => (
              <Link key={v} to={`/vendre-vente-a-terme/${v}`} className="text-sm border border-navy/20 px-3 py-1.5 hover:bg-navy hover:text-cream transition-colors capitalize" data-testid={`city-link-${v}`}>
                {v}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
